import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteProofLogo from '../../components/SiteProofLogo';

const Section = ({ number, title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-slate-100 mb-3">
      {number}. {title}
    </h2>
    <div className="space-y-3 text-sm text-slate-400 leading-relaxed">{children}</div>
  </section>
);

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="no-underline">
              <SiteProofLogo size="sm" />
            </Link>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SiteProof
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-2">
            Terms of Service
          </h1>
          <p className="text-sm text-slate-500 mt-2">Last updated: 28 March 2026</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Intro box */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8">
          <p className="text-sm text-slate-400">
            These Terms of Service ("<strong className="text-slate-200">Terms</strong>") govern your use of the SiteProof platform, operated by{' '}
            <strong className="text-slate-200">SiteProof Ltd</strong>, a company registered in England and Wales
            (Company number: <strong className="text-slate-200">17085500</strong>). By creating an account or using SiteProof,
            you agree to be bound by these Terms. Please read them carefully before using the platform.
          </p>
        </div>

        <Section number="1" title="Service Description">
          <p>
            SiteProof is a cloud-based construction site management platform that enables companies to manage
            drawings, documents, tasks, photos, snags, incidents, site reports, compliance records, and team
            communications across one or more construction sites.
          </p>
          <p>
            SiteProof Ltd grants you a limited, non-exclusive, non-transferable licence to access and use the
            SiteProof platform in accordance with these Terms and your chosen subscription plan. The features
            available to you depend on your subscription tier.
          </p>
          <p>
            The platform is provided on a software-as-a-service (SaaS) basis. SiteProof Ltd may update,
            improve, or modify the platform from time to time. We will notify you of significant changes where
            reasonably practicable.
          </p>
        </Section>

        <Section number="2" title="User Accounts">
          <p>To use SiteProof, you must create an account. You agree to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Provide accurate, complete, and up-to-date registration information</li>
            <li>Maintain the confidentiality of your account credentials and not share your password</li>
            <li>Notify SiteProof Ltd immediately if you suspect any unauthorised access to your account</li>
            <li>Ensure that all users added to your account (workers, managers, clients) are aware of and comply with these Terms</li>
            <li>Be responsible for all activity that occurs under your account</li>
          </ul>
          <p>
            You must be at least 18 years old and have the authority to enter into these Terms on behalf of
            your company. By creating an account, you confirm that you have this authority.
          </p>
          <p>
            SiteProof Ltd reserves the right to suspend or terminate accounts that contain inaccurate
            information or that are used in breach of these Terms.
          </p>
        </Section>

        <Section number="3" title="Subscription, Billing and Payments">
          <p>
            Access to SiteProof requires a valid subscription. Subscription fees are charged in advance on a
            monthly or annual basis, depending on your chosen plan.
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>All fees are quoted exclusive of VAT unless otherwise stated. VAT will be added at the applicable rate</li>
            <li>Payment is processed securely via Stripe. SiteProof Ltd does not store your card details</li>
            <li>Subscriptions renew automatically at the end of each billing period unless cancelled before the renewal date</li>
            <li>Refunds are not provided for partial subscription periods or unused time remaining on a plan</li>
            <li>Additional site licences (where applicable) are billed at the rate shown at the time of purchase</li>
          </ul>
          <p>
            SiteProof Ltd reserves the right to modify subscription pricing with at least 30 days' written
            notice. Continued use of the platform after a price change takes effect constitutes acceptance of
            the new pricing.
          </p>
          <p>
            If a payment fails, SiteProof Ltd may suspend access to the platform until payment is successfully
            processed. You are responsible for ensuring your payment details remain current and valid.
          </p>
        </Section>

        <Section number="4" title="Free Trial Terms">
          <p>
            SiteProof may offer a free trial period to new customers. During the free trial:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>You will have access to the platform features included in your chosen plan</li>
            <li>No charge will be made until the trial period ends</li>
            <li>You may be required to provide payment details at the start of the trial. If so, your subscription will begin automatically at the end of the trial period unless you cancel before it ends</li>
            <li>Free trials are available to new customers only. SiteProof Ltd reserves the right to decline or withdraw a free trial at its discretion</li>
          </ul>
          <p>
            To avoid being charged, you must cancel your subscription before the end of the trial period.
            Cancellation instructions are available in your account settings.
          </p>
        </Section>

        <Section number="5" title="Cancellation and Termination">
          <p>
            You may cancel your subscription at any time through your account settings or by contacting
            support@site-proof.app. Upon cancellation:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Your access to the platform will continue until the end of the current billing period</li>
            <li>No refund will be issued for the remaining time in the billing period</li>
            <li>You should export any data you wish to retain before your access ends</li>
            <li>SiteProof Ltd will retain your data for 30 days after your access ends, after which it may be permanently deleted</li>
          </ul>
          <p>
            SiteProof Ltd may suspend or terminate your account immediately and without notice if you:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Breach any provision of these Terms</li>
            <li>Fail to pay subscription fees when due</li>
            <li>Use the platform in a way that SiteProof Ltd reasonably considers harmful, abusive, or unlawful</li>
          </ul>
          <p>
            In the event of termination by SiteProof Ltd for breach, no refund will be provided.
          </p>
        </Section>

        <Section number="6" title="Acceptable Use">
          <p>You agree to use SiteProof only for lawful purposes and in accordance with these Terms. You must not:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Use the platform to upload, store, or transmit any content that is unlawful, harmful, defamatory, or infringes third-party intellectual property rights</li>
            <li>Attempt to gain unauthorised access to any part of the platform, its infrastructure, or other users' accounts</li>
            <li>Use the platform to send unsolicited communications or spam</li>
            <li>Reverse engineer, decompile, or attempt to extract the source code of the platform</li>
            <li>Resell, sublicence, or otherwise make the platform available to third parties without SiteProof Ltd's written consent</li>
            <li>Use the platform in any way that could damage, disable, or impair the platform or interfere with other users</li>
            <li>Upload malicious code, viruses, or any software designed to disrupt or damage systems</li>
          </ul>
          <p>
            SiteProof Ltd reserves the right to investigate and take appropriate action, including account
            suspension or termination, in response to any suspected breach of this acceptable use policy.
          </p>
        </Section>

        <Section number="7" title="Data Storage and Backups">
          <p>
            SiteProof stores your data on secure cloud infrastructure provided by Supabase and Amazon Web
            Services (AWS), with servers located in the United Kingdom and European Economic Area.
          </p>
          <p>
            SiteProof Ltd takes reasonable steps to back up platform data on a regular basis. However:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Backups are maintained for operational and disaster recovery purposes and are not a substitute for your own data management practices</li>
            <li>SiteProof Ltd does not guarantee that backups will be available for restoration in all circumstances</li>
            <li>You are responsible for exporting and retaining copies of any data that is critical to your business operations</li>
            <li>SiteProof Ltd is not liable for any loss of data arising from circumstances beyond its reasonable control</li>
          </ul>
          <p>
            Upon account termination, your data will be retained for 30 days to allow you to export it.
            After this period, data will be permanently deleted from live systems. Backup copies may persist
            for up to 90 days before being overwritten.
          </p>
        </Section>

        <Section number="8" title="Limitation of Liability">
          <p>
            To the maximum extent permitted by applicable law, SiteProof Ltd shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Loss of profits, revenue, or business</li>
            <li>Loss of data or inability to recover data</li>
            <li>Loss of goodwill or reputation</li>
            <li>Business interruption or downtime</li>
          </ul>
          <p>
            SiteProof Ltd's total aggregate liability to you in connection with these Terms shall not exceed
            the total subscription fees paid by you to SiteProof Ltd in the 12 months immediately preceding
            the event giving rise to the claim.
          </p>
          <p>
            Nothing in these Terms limits or excludes liability for death or personal injury caused by
            negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded
            or limited by applicable law.
          </p>
        </Section>

        <Section number="9" title="No Compliance Guarantee">
          <p>
            <strong className="text-slate-200">Important:</strong> SiteProof is a tool to help you organise,
            track, and record site activities, documents, and compliance-related information. It is not a
            compliance management service and does not guarantee that your operations meet any legal, regulatory,
            health and safety, or industry requirements.
          </p>
          <p>
            The customer (you) is solely responsible for:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Ensuring that your construction sites comply with all applicable laws, regulations, and industry standards, including but not limited to the Health and Safety at Work Act 1974, the Construction (Design and Management) Regulations 2015, and any other relevant legislation</li>
            <li>Verifying that documents, certificates, and records stored in SiteProof are accurate, current, and legally sufficient</li>
            <li>Ensuring that workers hold valid qualifications, certifications, and training as required by law or contract</li>
            <li>Meeting all obligations to your clients, principal contractors, and regulatory authorities</li>
          </ul>
          <p>
            SiteProof Ltd makes no representation or warranty that use of the platform will result in
            compliance with any legal or regulatory requirement. Any compliance-related features within the
            platform are provided as organisational aids only and should not be relied upon as legal advice.
          </p>
        </Section>

        <Section number="10" title="Intellectual Property">
          <p>
            All intellectual property rights in the SiteProof platform, including software, design, trademarks,
            and documentation, are owned by SiteProof Ltd or its licensors. Nothing in these Terms transfers
            any intellectual property rights to you.
          </p>
          <p>
            You retain ownership of all data and content you upload to SiteProof. By uploading content, you
            grant SiteProof Ltd a limited licence to store and process that content solely for the purpose of
            providing the service to you.
          </p>
        </Section>

        <Section number="11" title="Governing Law">
          <p>
            These Terms of Service are governed by and construed in accordance with the laws of England and
            Wales. Any disputes arising under or in connection with these Terms shall be subject to the
            exclusive jurisdiction of the courts of England and Wales.
          </p>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
            will continue in full force and effect.
          </p>
        </Section>

        <Section number="12" title="Contact">
          <p>
            If you have any questions about these Terms of Service, please contact SiteProof Ltd at:
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-3">
            <p className="font-medium text-slate-200">SiteProof Ltd</p>
            <p>Registered in England and Wales</p>
            <p>Company number: 17085500</p>
            <p>
              Email:{' '}
              <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
                support@site-proof.app
              </a>
            </p>
          </div>
        </Section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8 mt-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <SiteProofLogo size="sm" />
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link to="/privacy-policy" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/data-processing" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Data Processing Agreement
              </Link>
              <Link to="/cookie-policy" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Cookie Policy
              </Link>
              <Link to="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Back to SiteProof
              </Link>
            </div>
            <p className="text-xs text-slate-600">&copy; 2026 SiteProof Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
